<section class="create-task-form__body">
  <header class="create-task-form__header">
    <h2 class="create-task-form__title">{{ 'create-task-form.create-task' | translate }}</h2>
  </header>

  <form
    id="createTaskForm"
    #createTaskForm="ngForm"
    (ngSubmit)="onSubmit()"
    class="create-task-form__form"
  >
    <div class="create-task-form__field">
      <input
        id="title"
        name="title"
        required
        autocomplete="off"
        [ngModel]="store.title()"
        (ngModelChange)="store.title.set($event)"
        [placeholder]="'create-task-form.title-placeholder' | translate"
        [class.ng-invalid-visible]="titleInput.invalid && titleInput.touched"
        [attr.aria-describedby]="titleInput.invalid && titleInput.touched ? 'title-error' : null"
        [attr.aria-invalid]="(titleInput.invalid && titleInput.touched) || null"
        #titleInput="ngModel"
        class="create-task-form__input"
      />
    </div>
    @if (titleInput.touched && titleInput.invalid) {
      <small id="title-error" class="create-task-form__error">
        @if (titleInput.errors?.['required']) {
          {{ 'form.errors.required' | translate }}
        }
      </small>
    }

    <div class="create-task-form__field">
      <textarea
        id="description"
        name="description"
        rows="3"
        [ngModel]="store.description()"
        (ngModelChange)="store.description.set($event)"
        [placeholder]="'create-task-form.description-placeholder' | translate"
        #descriptionInput="ngModel"
        class="create-task-form__textarea"
      ></textarea>
    </div>

    @if (isEmbedded()) {
      <div class="create-task-form__field create-task-form__scene-section">
        <span class="create-task-form__label">{{ 'create-task-form.scene' | translate }}</span>
        @if (store.hasScene()) {
          <div class="create-task-form__scene-preview">
            <ocp-scene-thumbnail [thumbnailUrl]="store.sceneThumbnailUrl()">
              <button
                hlmBtn
                type="button"
                variant="destructive"
                size="icon"
                class="create-task-form__remove-scene-button"
                (click)="onRemoveScene()"
                [attr.aria-label]="'create-task-form.remove-scene' | translate"
              >
                <ocp-themed-icon name="trash.svg" [size]="20" />
              </button>
            </ocp-scene-thumbnail>
          </div>
        } @else {
          <div class="create-task-form__add-scene-placeholder">
            <ocp-themed-icon name="skyscraper.svg" [size]="54" />
            <button
              hlmBtn
              type="button"
              variant="outline"
              class="create-task-form__add-scene-button"
              (click)="onAddSceneThrottled()"
            >
              <ocp-themed-icon
                name="plus-circled-bold.svg"
                [size]="20"
                class="create-task-form__add-scene-icon"
              />
              {{ 'create-task-form.add-scene-in-model' | translate }}
            </button>
          </div>
        }
      </div>
    }

    <div class="create-task-form__field create-task-assignee-dropdown">
      <span class="create-task-form__label">{{ 'create-task-form.assignee' | translate }}</span>
      <ocp-member-multiselect-input
        [value]="store.selectedAssigneeIds()"
        [options]="memberDropdownOptions"
        [ariaLabel]="'create-task-form.assignee' | translate"
        [placeholder]="('create-task-form.select-assignee' | translate) + '...'"
        (membersChange)="onAssigneesChange($event)"
      />
    </div>

    <div class="create-task-form__row">
      <div
        class="create-task-form__field create-task-form__field--half create-task-priority-dropdown"
      >
        <span class="create-task-form__label">{{ 'create-task-form.priority' | translate }}</span>
        <ocp-priority-dropdown
          [value]="store.priority()"
          [ariaLabel]="'create-task-form.priority' | translate"
          [placeholder]="('create-task-form.select-priority' | translate) + '...'"
          [chevronSize]="18"
          [chevronIcon]="'chevron-down-bold.svg'"
          (priorityChange)="onPriorityChange($event)"
        />
      </div>
      <div
        class="create-task-form__field create-task-form__field--half create-task-status-dropdown"
      >
        <span class="create-task-form__label">{{ 'create-task-form.status' | translate }}</span>
        <ocp-status-dropdown
          [value]="store.status()"
          [ariaLabel]="'create-task-form.status' | translate"
          [placeholder]="('create-task-form.select-status' | translate) + '...'"
          [chevronSize]="18"
          [chevronIcon]="'chevron-down-bold.svg'"
          (statusChange)="onStatusChange($event)"
        />
      </div>
    </div>

    <div class="create-task-form__field create-task-due-date-picker">
      <span class="create-task-form__label">{{ 'create-task-form.due-date' | translate }}</span>
      <ocp-due-date-picker
        [value]="store.dueDate()"
        [ariaLabel]="'create-task-form.due-date' | translate"
        [placeholder]="('create-task-form.select-date' | translate) + '...'"
        [chevronSize]="18"
        [chevronIcon]="'chevron-down-bold.svg'"
        (dateChange)="onDueDateChange($event)"
      />
    </div>

    <hlm-accordion
      class="w-full create-task-form__more-details overflow-visible rounded-none border-0"
    >
      <hlm-accordion-item
        class="border-0 data-open:bg-transparent"
        [isOpened]="store.showMoreDetails()"
        (openedChange)="onMoreDetailsOpenedChange($event)"
      >
        <hlm-accordion-trigger
          triggerClass="w-fit flex-none items-center gap-2 p-0 font-normal text-[color:var(--corporate-text-primary)] hover:no-underline **:data-[slot=accordion-trigger-icon]:hidden"
        >
          <ocp-themed-icon
            name="chevron-down.svg"
            [size]="9"
            class="create-task-form__more-details-chevron"
            [class.create-task-form__more-details-chevron--open]="store.showMoreDetails()"
          />
          {{
            (store.showMoreDetails()
              ? 'create-task-form.less-details'
              : 'create-task-form.more-details'
            ) | translate
          }}
        </hlm-accordion-trigger>
        <hlm-accordion-content class="create-task-form__more-details-content px-0 *:pt-3! *:pb-0!">
          <div class="create-task-form__field create-task-tags-input">
            <span class="create-task-form__label">{{
              'create-task-form.tags-label' | translate
            }}</span>
            <ocp-tags-input
              [value]="tags()"
              [placeholder]="('create-task-form.add-tags' | translate) + '...'"
              (valueChange)="onTagsChange($event)"
            />
          </div>

          <div
            class="create-task-form__field create-task-form__field--spaced create-task-watcher-dropdown"
          >
            <span class="create-task-form__label">{{
              'create-task-form.watchers' | translate
            }}</span>
            <ocp-member-multiselect-input
              [value]="store.selectedWatcherIds()"
              [options]="memberDropdownOptions"
              [ariaLabel]="'create-task-form.watchers' | translate"
              [placeholder]="('create-task-form.select-watchers' | translate) + '...'"
              (membersChange)="onWatchersChange($event)"
            />
          </div>
        </hlm-accordion-content>
      </hlm-accordion-item>
    </hlm-accordion>
  </form>

  <div class="create-task-form__actions">
    <button hlmBtn type="button" variant="secondary" (click)="onCancel()">
      <ocp-themed-icon name="cross.svg" />
      {{ 'create-task-form.cancel' | translate }}
    </button>
    <button hlmBtn type="button" (click)="submitForm()">
      <ocp-themed-icon name="checkmark.svg" mode="swap" />
      {{ 'create-task-form.create-task' | translate }}
    </button>
  </div>
</section>
